import Vue from 'vue'
import i18n from '@/plugins/i18n'
import store from '@/store'

export const HISTORY_MAX = 50

export const HISTORY_MENU_MAX = 12

export const HISTORY_COALESCE_MS = 1500

export const HISTORY_TOAST_MS = 5000

export const history = Vue.observable({
    contexts: {},
    toast: null,
})

const flushers = {}

const shortcutContexts = []

let toastSeq = 0

const blankContext = () => ({ done: [], undone: [], busy: false })

const contextOf = (context) => {
    if (!history.contexts[context]) Vue.set(history.contexts, context, blankContext())
    return history.contexts[context]
}

const showToast = (context, label, mode) => {
    history.toast = { id: ++toastSeq, context, label, mode }
}

export const hideHistoryToast = (id = null) => {
    if (!history.toast) return
    if (id === null || history.toast.id === id) history.toast = null
}

export const resetHistory = (context) => {
    Vue.set(history.contexts, context, blankContext())
    if (history.toast?.context === context) history.toast = null
}

export const canUndo = (context) => (history.contexts[context]?.done.length || 0) > 0

export const canRedo = (context) => (history.contexts[context]?.undone.length || 0) > 0

export const lastDone = (context) => {
    const done = history.contexts[context]?.done || []
    return done[done.length - 1] || null
}

export const lastUndone = (context) => {
    const undone = history.contexts[context]?.undone || []
    return undone[undone.length - 1] || null
}

export const historyEntries = (context) => (history.contexts[context]?.done || [])
    .slice(-HISTORY_MENU_MAX)
    .reverse()

export const pushHistory = (context, command, { toast = true } = {}) => {
    const state = contextOf(context)
    const now = Date.now()
    const top = state.done[state.done.length - 1]
    let entry = top
    if (command.key && top && top.key === command.key && now - top.at < HISTORY_COALESCE_MS) {
        top.redo = command.redo
        top.label = command.label
        top.at = now
    } else {
        entry = { label: command.label, key: command.key || null, owner: command.owner || null, undo: command.undo, redo: command.redo, at: now }
        state.done.push(entry)
        if (state.done.length > HISTORY_MAX) state.done.shift()
    }
    state.undone = []
    if (toast) showToast(context, command.label, 'undo')
    return entry
}

export const removeHistoryEntry = (context, entry) => {
    const state = history.contexts[context]
    if (!state || !entry) return
    state.done = state.done.filter(item => item !== entry)
    state.undone = state.undone.filter(item => item !== entry)
    if (history.toast?.context === context && history.toast.label === entry.label) history.toast = null
}

export const dropHistoryEntries = (context, owner) => {
    const state = history.contexts[context]
    if (!state || !owner) return
    state.done = state.done.filter(command => command.owner !== owner)
    state.undone = state.undone.filter(command => command.owner !== owner)
}

export const execHistory = async (context, command, options = {}) => {
    await command.redo()
    pushHistory(context, command, options)
}

export const registerHistoryFlusher = (context, fn) => {
    if (!flushers[context]) flushers[context] = new Set()
    flushers[context].add(fn)
    return () => {
        if (flushers[context]) flushers[context].delete(fn)
    }
}

const flush = async (context) => {
    const fns = [...(flushers[context] || [])]
    await Promise.all(fns.map(fn => fn()))
}

const run = async (context, fn) => {
    const state = contextOf(context)
    if (state.busy) return false
    state.busy = true
    try {
        await fn(state)
        return true
    } catch (error) {
        console.error('[history]', context, error)
        store.commit('showSnackBar', { text: i18n.t('common.an_error_has_occurred'), color: 'error' })
        return false
    } finally {
        state.busy = false
    }
}

export const undoHistory = async (context) => {
    await flush(context)
    return run(context, async (state) => {
        const command = state.done[state.done.length - 1]
        if (!command) return
        await command.undo()
        state.done.pop()
        state.undone.push(command)
        showToast(context, command.label, 'redo')
    })
}

export const redoHistory = async (context) => {
    await flush(context)
    return run(context, async (state) => {
        const command = state.undone[state.undone.length - 1]
        if (!command) return
        await command.redo()
        state.undone.pop()
        state.done.push(command)
        showToast(context, command.label, 'undo')
    })
}

export const undoHistoryTo = async (context, steps) => {
    await flush(context)
    return run(context, async (state) => {
        let count = 0
        for (let i = 0; i < steps; i++) {
            const command = state.done[state.done.length - 1]
            if (!command) break
            await command.undo()
            state.done.pop()
            state.undone.push(command)
            count++
        }
        if (count) showToast(context, i18n.tc('history.undone_count', count, { n: count }), 'redo')
    })
}

export const isMacPlatform = () => /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '')

const isTypingTarget = (target) => {
    if (!target) return false
    const tag = (target.tagName || '').toLowerCase()
    return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable
}

const onKeydown = (event) => {
    const context = shortcutContexts[shortcutContexts.length - 1]
    if (!context || isTypingTarget(event.target)) return
    if (!(event.metaKey || event.ctrlKey) || event.altKey) return
    const key = (event.key || '').toLowerCase()
    const redo = (key === 'z' && event.shiftKey) || (key === 'y' && !event.shiftKey)
    const undo = key === 'z' && !event.shiftKey
    if (!undo && !redo) return
    event.preventDefault()
    if (redo) redoHistory(context)
    else undoHistory(context)
}

export const enableHistoryShortcuts = (context) => {
    disableHistoryShortcuts(context)
    shortcutContexts.push(context)
    if (shortcutContexts.length === 1) window.addEventListener('keydown', onKeydown)
}

export const disableHistoryShortcuts = (context) => {
    const index = shortcutContexts.lastIndexOf(context)
    if (index === -1) return
    shortcutContexts.splice(index, 1)
    if (shortcutContexts.length === 0) window.removeEventListener('keydown', onKeydown)
}

const clone = (value) => JSON.parse(JSON.stringify(value === undefined ? null : value))

export const createSnapshotTracker = ({ context, read, write, describe = null, owner = null }) => {
    let base = clone(read())
    let timer = null

    const apply = async (value) => {
        await write(clone(value))
        base = clone(value)
    }

    const commit = (label = null, key = null) => {
        if (timer) { clearTimeout(timer); timer = null }
        const after = clone(read())
        if (JSON.stringify(after) === JSON.stringify(base)) return false
        const before = base
        base = after
        const text = label || (describe && describe(before, after)) || i18n.t('history.change')
        pushHistory(context, { label: text, key: key || text, owner, undo: () => apply(before), redo: () => apply(after) })
        return true
    }

    const schedule = (delay = 600) => {
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => { timer = null; commit() }, delay)
    }

    const sync = () => {
        if (timer) { clearTimeout(timer); timer = null }
        base = clone(read())
    }

    const unregister = registerHistoryFlusher(context, () => { if (timer) commit() })

    const destroy = () => {
        if (timer) { clearTimeout(timer); timer = null }
        unregister()
    }

    return { commit, schedule, sync, destroy }
}

const quote = (name) => `«${name || i18n.tc('common.exercises', 1)}»`

const blockLabel = (block, index) => block?.name || `${i18n.tc('common.blocks', 1)} ${index + 1}`

const withoutUid = (item) => {
    const copy = clone(item)
    if (copy && typeof copy === 'object') delete copy.uid
    return JSON.stringify(copy)
}

const findInserted = (before, after) => {
    const used = new Set()
    for (let i = 0; i < after.length; i++) {
        const key = JSON.stringify(after[i])
        const match = before.findIndex((item, j) => !used.has(j) && JSON.stringify(item) === key)
        if (match === -1) return i
        used.add(match)
    }
    return after.length - 1
}

const findRemoved = (before, after) => findInserted(after, before)

const sameItems = (before, after) => {
    const sort = list => list.map(item => JSON.stringify(item)).sort()
    return JSON.stringify(sort(before)) === JSON.stringify(sort(after))
}

export const describeExercisesChange = (before = [], after = []) => {
    if (after.length > before.length) {
        const index = findInserted(before, after)
        const exercise = after[index]
        const isCopy = before.some(item => withoutUid(item) === withoutUid(exercise))
        return i18n.t(isCopy ? 'history.duplicate' : 'history.add', { name: quote(exercise?.name) })
    }
    if (after.length < before.length) {
        const exercise = before[findRemoved(before, after)]
        return i18n.t('history.delete', { name: quote(exercise?.name) })
    }
    if (sameItems(before, after)) return i18n.t('history.reorder_exercises')
    const index = after.findIndex((item, i) => JSON.stringify(item) !== JSON.stringify(before[i]))
    const prev = before[index]
    const next = after[index]
    if ((prev?.config?.sets?.length || 0) !== (next?.config?.sets?.length || 0)) {
        return i18n.t('history.sets_of', { name: quote(next?.name) })
    }
    return i18n.t('history.edit', { name: quote(next?.name) })
}

export const describeBlocksChange = (before = [], after = []) => {
    if (after.length > before.length) {
        const index = findInserted(before, after)
        const block = after[index]
        const isCopy = before.some(item => withoutUid(item) === withoutUid(block))
        if (isCopy) return i18n.t('history.duplicate', { name: blockLabel(block, index) })
        return i18n.t('history.add_block')
    }
    if (after.length < before.length) {
        const index = findRemoved(before, after)
        return i18n.t('history.delete', { name: blockLabel(before[index], index) })
    }
    if (sameItems(before, after)) return i18n.t('history.reorder_blocks')
    const index = after.findIndex((item, i) => JSON.stringify(item) !== JSON.stringify(before[i]))
    const prev = before[index] || {}
    const next = after[index] || {}
    if ((prev.name || '') !== (next.name || '')) return i18n.t('history.rename', { name: blockLabel(next, index) })
    if (JSON.stringify(prev.exercises || []) !== JSON.stringify(next.exercises || [])) {
        return describeExercisesChange(prev.exercises || [], next.exercises || [])
    }
    return i18n.t('history.edit', { name: blockLabel(next, index) })
}
